import { useState, useEffect, useCallback } from 'react';
import { 
  getUserJobs,
  AsyncProcessingJob 
} from '../utils/documentUpload/asyncProcessing';

/**
 * Hook to track active and completed document processing jobs
 * Loads jobs ONLY from database (no localStorage)
 * Automatically polls for updates
 */
export const useDocumentProcessingJobs = (autoRefreshInterval: number = 3000) => {
  const [activeJobs, setActiveJobs] = useState<AsyncProcessingJob[]>([]);
  const [completedJobs, setCompletedJobs] = useState<AsyncProcessingJob[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshActiveJobs = useCallback(async () => {
    console.log('[useDocumentProcessingJobs] Refreshing jobs from database...');
    setLoading(true);
    try {
      // Load ALL jobs from database (single source of truth)
      const dbJobs = await getUserJobs();
      console.log(`[useDocumentProcessingJobs] Retrieved ${dbJobs.length} jobs from database`);

      // Separate active and completed
      const active = dbJobs.filter(j => j.status === 'processing');
      const completed = dbJobs.filter(j => j.status === 'completed' || j.status === 'failed');

      console.log(`[useDocumentProcessingJobs] Active: ${active.length}, Completed: ${completed.length}`);

      setActiveJobs(active);
      setCompletedJobs(completed.slice(0, 20)); // Keep only last 20
    } catch (error) {
      console.error('[useDocumentProcessingJobs] Failed to refresh jobs from database:', error);
      // Don't fallback to localStorage - show error or empty state
      setActiveJobs([]);
      setCompletedJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    console.log('[useDocumentProcessingJobs] Loading jobs from database...');
    await refreshActiveJobs();
  }, [refreshActiveJobs]);

  // Load initial data from database
  useEffect(() => {
    console.log('[useDocumentProcessingJobs] Hook mounted, loading jobs from database...');
    loadJobs();
  }, [loadJobs]);

  // Auto-refresh active jobs
  useEffect(() => {
    if (activeJobs.length === 0) {
      console.log('[useDocumentProcessingJobs] No active jobs, skipping auto-refresh');
      return;
    }

    console.log(`[useDocumentProcessingJobs] Setting up auto-refresh for ${activeJobs.length} active jobs (interval: ${autoRefreshInterval}ms)`);
    const interval = setInterval(() => {
      refreshActiveJobs();
    }, autoRefreshInterval);

    return () => {
      console.log('[useDocumentProcessingJobs] Clearing auto-refresh interval');
      clearInterval(interval);
    };
  }, [activeJobs.length, autoRefreshInterval, refreshActiveJobs]);

  const hasActiveJobs = activeJobs.length > 0;
  const hasCompletedJobs = completedJobs.length > 0;

  console.log(`[useDocumentProcessingJobs] Current state - Active: ${activeJobs.length}, Completed: ${completedJobs.length}, Loading: ${loading}`);

  return {
    activeJobs,
    completedJobs,
    hasActiveJobs,
    hasCompletedJobs,
    loading,
    refreshActiveJobs,
    loadJobs
  };
};
